import { lazy } from 'react'
import { AppPlugins } from 'app/constants'
import Plugin from 'core/plugins/plugin'
import { isOpenCloud } from 'core/utils/helpers'
import { READONLY_ROLES } from 'core/utils/rbac'
import Dashboard from 'openstack/components/dashboard/Dashboard'
import { openstackLogo } from '../openstack/logo'
import { kubernetesNav, kubernetesRoutesMapping } from './kubernetesNav'

// Lazy load all route components for optimal code splitting
const ClusterBlueprintPage = lazy(
  () => import('app/plugins/openstack/components/infrastructure/blueprint/ClusterBlueprintPage'),
)
const FlavorsListPage = lazy(() => import('openstack/components/flavors/FlavorsListPage'))
const ImagesListPage = lazy(() => import('openstack/components/images/ImagesListPage'))
const ImageDetailsPage = lazy(() => import('openstack/components/images/details/ImageDetailsPage'))
const HostAggregatesListPage = lazy(
  () => import('openstack/components/infrastructure/host-aggregates/HostAggregatesListPage'),
)
const StretchedClustersListPage = lazy(
  () =>
    import(
      'openstack/components/infrastructure/stretched-clusters/components/StretchedClustersListPage'
    ),
)
const StretchedClusterDetailsPage = lazy(
  () =>
    import(
      'openstack/components/infrastructure/stretched-clusters/components/StretchedClusterDetailsPage'
    ),
)
const AddVmwareGatewayPage = lazy(
  () => import('openstack/components/infrastructure/hosts/AddVmwareGatewayPage'),
)
const HostsListPage = lazy(() => import('openstack/components/infrastructure/hosts/HostsListPage'))
const HostDetailsPage = lazy(
  () => import('openstack/components/infrastructure/hosts/host-details/HostDetailsPage'),
)
const NetworksListPage = lazy(() => import('openstack/components/networks/NetworksListPage'))
const FloatingIpsListPage = lazy(
  () => import('openstack/components/networks/floating-ips/FloatingIpsListPage'),
)
const NetworkDetailsPage = lazy(
  () => import('openstack/components/networks/network-details/NetworkDetailsPage'),
)
const RouterDetailsPage = lazy(
  () => import('openstack/components/networks/routers/RouterDetailsPage'),
)
const RoutersListPage = lazy(() => import('openstack/components/networks/routers/RoutersListPage'))
const SecurityGroupsListPage = lazy(
  () => import('openstack/components/networks/security-groups/SecurityGroupsListPage'),
)
const VirtualNetworkDetailsPage = lazy(
  () => import('openstack/components/networks/virtual-networks/VirtualNetworkDetailsPage'),
)
const VirtualNetworksListPage = lazy(
  () => import('openstack/components/networks/virtual-networks/VirtualNetworksListPage'),
)
const VmwareNetworkDetailsPage = lazy(
  () => import('openstack/components/networks/vmware-networks/VmwareNetworkDetailsPage'),
)
const VmwareNetworksListPage = lazy(
  () => import('openstack/components/networks/vmware-networks/VmwareNetworksListPage'),
)
const StackDetailsPage = lazy(() => import('openstack/components/orchestration/StackDetailsPage'))
const StacksListPage = lazy(() => import('openstack/components/orchestration/StacksListPage'))
const SshKeysListPage = lazy(() => import('openstack/components/security/keys/SshKeysListPage'))

const StorageDashboardPage = lazy(
  () => import('openstack/components/storage/dashboard/StorageDashboardPage'),
)
const VolumeSnapshotsListPage = lazy(
  () => import('openstack/components/storage/snapshots/VolumeSnapshotsListPage'),
)
const VolumeSnapshotDetailsPage = lazy(
  () => import('openstack/components/storage/snapshots/details/VolumeSnapshotDetailsPage'),
)
const VolumeTypesListPage = lazy(
  () => import('openstack/components/storage/volume-types/VolumeTypesListPage'),
)
const VolumeTypeDetailsPage = lazy(
  () => import('openstack/components/storage/volume-types/details/VolumeTypeDetailsPage'),
)
const VolumesListPage = lazy(() => import('openstack/components/storage/volumes/VolumesListPage'))
const VolumeDetailsPage = lazy(
  () => import('openstack/components/storage/volumes/volume-details/VolumeDetailsPage'),
)
const VirtualMachinesListPage = lazy(
  () => import('openstack/components/vms/VirtualMachinesListPage'),
)
const ServerGroupsListPage = lazy(
  () => import('openstack/components/vms/server-groups/ServerGroupsListPage'),
)
const ServerGroupDetailsPage = lazy(
  () => import('openstack/components/vms/server-groups/details/ServerGroupDetailsPage'),
)
const VmDetailsPage = lazy(() => import('openstack/components/vms/vm-details/VmDetailsPage'))
const LoadBalancerDetailsPage = lazy(
  () => import('./components/networks/load-balancers/LoadBalancerDetailsPage'),
)
const NotificationsPage = lazy(() => import('core/components/notifications/NotificationsPage'))
const DnsTabsPage = lazy(() => import('app/plugins/openstack/components/networks/dns/DnsTabsPage'))
const ClusterHostGroupsListPage = lazy(
  () => import('./components/infrastructure/clusters/ClusterHostGroupsListPage'),
)
const ClusterHostGroupDetailsPage = lazy(
  () => import('./components/infrastructure/clusters/details/ClusterHostGroupDetailsPage'),
)
const ServiceHealthDetailsPage = lazy(
  () =>
    import('app/plugins/infrastructure/components/common/service-health/ServiceHealthDetailsPage'),
)
const RepositoryListPage = lazy(
  () => import('./components/orchestration/repositories/RepositoryListPage'),
)
const AppsDetailsPage = lazy(() => import('./components/orchestration/app-catalog/AppsDetailsPage'))
const GpuPage = lazy(() => import('./components/infrastructure/gpu/GpuPage'))

const openstackPlugin = new Plugin(
  AppPlugins.OpenStack,
  'Virtualized Clusters',
  '/ui/pcd',
  openstackLogo,
  'Virtualized clusters for traditional workloads',
)

// Guard style: use `requiredRoles: ['admin', 'cluster_admin', 'platform_admin']` (allow-list)
// for hard system/domain-tier gates; use `bannedRoles: READONLY_ROLES` (deny-list) for
// ordinary create/edit actions where only 'reader' should be excluded.
export const openstackRoutes = [
  {
    name: 'Dashboard',
    link: { path: 'dashboard', exact: true, default: isOpenCloud() ? false : true },
    component: Dashboard,
  },
  {
    name: 'Virtual Machines',
    link: {
      path: '/compute/vms/:vmstab',
      navPath: '/compute/vms/:vmstab',
      exact: true,
    },
    component: VirtualMachinesListPage,
  },
  {
    name: 'Virtual Machines Deploy',
    bannedRoles: READONLY_ROLES,
    link: { path: '/compute/vms/:vmstab/deploy', exact: true },
    component: VirtualMachinesListPage,
  },
  {
    name: 'Virtual Machine Details',
    link: { path: '/compute/vms/list/:id/:tab?', exact: true },
    component: VmDetailsPage,
  },
  {
    name: 'Server Groups',
    link: { path: '/compute/server-groups', exact: true },
    component: ServerGroupsListPage,
  },
  {
    name: 'Server Groups Create',
    bannedRoles: READONLY_ROLES,
    requiredPermissions: [
      ['nova', 'os_compute_api.os_server_groups.create:POST:/os-server-groups'],
    ],
    link: { path: '/compute/server-groups/create', exact: true },
    component: ServerGroupsListPage,
  },
  {
    name: 'Server Group Details',
    link: { path: '/compute/server-groups/:id/:tab?', exact: true },
    component: ServerGroupDetailsPage,
  },
  {
    name: 'Notifications',
    link: { path: '/notifications/:notificationType/:id?', exact: true },
    component: NotificationsPage,
  },
  {
    name: 'Images and VM Snapshots',
    link: { path: '/images/:tab?', exact: true },
    component: ImagesListPage,
  },
  {
    name: 'Images and VM Snapshots Create',
    bannedRoles: READONLY_ROLES,
    requiredPermissions: [['glance', 'add_image:POST:/v2/images']],
    link: { path: '/images/:tab?/(add|import)', exact: true },
    component: ImagesListPage,
  },
  {
    name: 'Image Details',
    link: { path: '/images/:id/:detailsTab?', exact: true },
    component: ImageDetailsPage,
  },
  {
    name: 'Flavors',
    link: { path: '/flavors', exact: true },
    component: FlavorsListPage,
  },
  {
    name: 'Flavors Create',
    bannedRoles: READONLY_ROLES,
    requiredPermissions: [['nova', 'os_compute_api.os_flavor_manage.create:POST:/flavors']],
    link: { path: '/flavors/create', exact: true },
    component: FlavorsListPage,
  },
  {
    name: 'Storage Dashboard',
    link: { path: '/storage/dashboard', exact: true },
    component: StorageDashboardPage,
  },
  {
    name: 'Volumes',
    link: { path: '/storage/volumes', exact: true },
    component: VolumesListPage,
  },
  {
    name: 'Volumes Create',
    bannedRoles: READONLY_ROLES,
    requiredPermissions: [['cinder', 'volume.create:POST:/volumes']],
    link: { path: '/storage/volumes/create', exact: true },
    component: VolumesListPage,
  },
  {
    name: 'Volume Details',
    link: { path: '/storage/volumes/:id/:tab?', exact: true },
    component: VolumeDetailsPage,
  },
  {
    name: 'Volume Types',
    link: { path: '/storage/volume-types', exact: true },
    component: VolumeTypesListPage,
  },
  {
    name: 'Volume Types Create',
    bannedRoles: READONLY_ROLES,
    requiredPermissions: [['cinder', 'volume_extension.type_create:POST:/types']],
    link: { path: '/storage/volume-types/create', exact: true },
    component: VolumeTypesListPage,
  },
  {
    name: 'Volume Type Details',
    link: { path: '/storage/volume-types/:id/:tab?', exact: true },
    component: VolumeTypeDetailsPage,
  },
  {
    name: 'Volume Snapshots',
    link: { path: '/storage/snapshots', exact: true },
    component: VolumeSnapshotsListPage,
  },
  {
    name: 'Volume Snapshot Details',
    link: { path: '/storage/snapshots/:id/:tab?', exact: true },
    component: VolumeSnapshotDetailsPage,
  },
  {
    name: 'Networks',
    link: { path: '/networking/networks', exact: true },
    component: NetworksListPage,
  },
  {
    name: 'Networks Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/networks/create', exact: true },
    component: NetworksListPage,
  },
  {
    name: 'Create Physical Network',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/networks/create-physical-network', exact: true },
    component: NetworksListPage,
  },
  {
    name: 'Create Virtual Network',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/networks/create-virtual-network', exact: true },
    component: NetworksListPage,
  },
  {
    name: 'Network Details',
    link: { path: '/networking/networks/:id/:tab?', exact: true },
    component: NetworkDetailsPage,
  },
  {
    name: 'Network Details Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/networks/:id/:tab/create', exact: true },
    component: NetworkDetailsPage,
  },
  {
    name: 'Virtual Networks',
    link: { path: '/networking/virtual-networks', exact: true },
    component: VirtualNetworksListPage,
  },
  {
    name: 'Virtual Networks Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/virtual-networks/create', exact: true },
    component: VirtualNetworksListPage,
  },
  {
    name: 'Virtual Network Details',
    link: { path: '/networking/virtual-networks/:id/:tab?', exact: true },
    component: VirtualNetworkDetailsPage,
  },
  {
    name: 'Virtual Network Details Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/virtual-networks/:id/:tab/create', exact: true },
    component: VirtualNetworkDetailsPage,
  },
  {
    name: 'Networks',
    link: { path: '/networking/vmw-networks', exact: true },
    component: VmwareNetworksListPage,
  },
  {
    name: 'Networks Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/vmw-networks/create', exact: true },
    component: VmwareNetworksListPage,
  },
  {
    name: 'Network Details',
    link: { path: '/networking/vmw-networks/:id/:tab?', exact: true },
    component: VmwareNetworkDetailsPage,
  },
  {
    name: 'Network Details Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/vmw-networks/:id/:tab/create', exact: true },
    component: VmwareNetworkDetailsPage,
  },
  {
    name: 'Routers',
    link: { path: '/networking/routers', exact: true },
    component: RoutersListPage,
  },
  {
    name: 'Routers Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/routers/create', exact: true },
    component: RoutersListPage,
  },
  {
    name: 'Router Details',
    link: { path: '/networking/routers/:id/:tab?', exact: true },
    component: RouterDetailsPage,
  },
  {
    name: 'Router Details Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/routers/:id/:tab/create', exact: true },
    component: RouterDetailsPage,
  },
  {
    name: 'Public IPs',
    link: { path: '/networking/public-ips', exact: true },
    component: FloatingIpsListPage,
  },
  {
    name: 'Public IPs Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/public-ips/create', exact: true },
    component: FloatingIpsListPage,
  },
  {
    name: 'Security Groups',
    link: { path: '/networking/security-groups', exact: true },
    component: SecurityGroupsListPage,
  },
  {
    name: 'Security Groups Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/security-groups/create', exact: true },
    component: SecurityGroupsListPage,
  },
  {
    name: 'DNS',
    link: { path: '/networking/dns/:tab', exact: true },
    component: DnsTabsPage,
  },
  {
    name: 'DNS Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/dns/:tab/create', exact: true },
    component: DnsTabsPage,
  },
  {
    name: 'Edit DNS',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/dns/:tab/edit/:id', exact: true },
    component: DnsTabsPage,
  },
  {
    name: 'Load Balancers Details',
    link: { path: '/networking/load-balancers/:tab?', exact: true },
    component: LoadBalancerDetailsPage,
  },
  {
    name: 'Load Balancers Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/networking/load-balancers/:tab?/create', exact: true },
    component: LoadBalancerDetailsPage,
  },
  {
    name: 'Cluster Blueprint',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/blueprint', exact: true },
    component: ClusterBlueprintPage,
  },
  {
    name: 'Cluster Hosts',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/hosts/:hoststab/(add)?', exact: true },
    component: HostsListPage,
  },
  {
    name: 'Add VMware Gateway',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/hosts/list/add-gateway', exact: true },
    component: AddVmwareGatewayPage,
  },
  {
    name: 'Cluster Host Details',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/hosts/list/:id/:tab?', exact: true },
    component: HostDetailsPage,
  },
  {
    name: 'Host Aggregates',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/host-aggregates/(create)?', exact: true },
    component: HostAggregatesListPage,
  },
  {
    name: 'Stretched Clusters',
    requiredRoles: ['admin'],
    link: { path: '/infrastructure/stretched-cluster', exact: true },
    component: StretchedClustersListPage,
  },
  {
    name: 'Stretched Cluster Details',
    requiredRoles: ['admin'],
    link: { path: '/infrastructure/stretched-cluster/:id/:tab?', exact: true },
    component: StretchedClusterDetailsPage,
  },
  {
    name: 'GPU Hosts',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/gpu/:tab?', exact: true },
    component: GpuPage,
  },
  {
    name: 'Ssh Keys',
    link: { path: '/ssh-key', exact: true },
    component: SshKeysListPage,
  },
  {
    name: 'Ssh Keys Import',
    bannedRoles: READONLY_ROLES,
    requiredPermissions: [['nova', 'os_compute_api.os_keypairs.create:POST:/os-keypairs']],
    link: { path: '/ssh-key/import', exact: true },
    component: SshKeysListPage,
  },
  {
    name: 'Stacks',
    link: { path: '/orchestration/stacks', exact: true },
    component: StacksListPage,
  },
  {
    name: 'Stacks Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/orchestration/stacks/create', exact: true },
    component: StacksListPage,
  },
  {
    name: 'Stack Details',
    link: { path: '/orchestration/stacks/:name/:id/:tab?', exact: true },
    component: StackDetailsPage,
  },
  {
    name: 'Apps',
    link: { path: '/orchestration/apps/:tab?', exact: true },
    component: AppsDetailsPage,
    // AFC-FR-002: See AppCatalogFeatureGate.spec.md
    requiredFeatures: (features) => features?.app_catalog?.enabled === true,
  },
  {
    name: 'Apps Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/orchestration/apps/:tab?/create', exact: true },
    component: AppsDetailsPage,
    // AFC-FR-002: See AppCatalogFeatureGate.spec.md
    requiredFeatures: (features) => features?.app_catalog?.enabled === true,
  },
  {
    name: 'Apps Template',
    bannedRoles: READONLY_ROLES,
    link: { path: '/orchestration/apps/:tab?/template', exact: true },
    component: AppsDetailsPage,
    // AFC-FR-002: See AppCatalogFeatureGate.spec.md
    requiredFeatures: (features) => features?.app_catalog?.enabled === true,
  },
  {
    name: 'Git Repository',
    link: { path: '/orchestration/repository', exact: true },
    component: RepositoryListPage,
    // AFC-FR-002: See AppCatalogFeatureGate.spec.md
    requiredFeatures: (features) => features?.app_catalog?.enabled === true,
  },
  {
    name: 'Git Repository Create',
    bannedRoles: READONLY_ROLES,
    link: { path: '/orchestration/repository/create', exact: true },
    component: RepositoryListPage,
    // AFC-FR-002: See AppCatalogFeatureGate.spec.md
    requiredFeatures: (features) => features?.app_catalog?.enabled === true,
  },
  {
    name: 'Clusters',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/clusters/(create)?', exact: true },
    component: ClusterHostGroupsListPage,
  },
  {
    name: 'Cluster Details',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/infrastructure/clusters/:id', exact: true },
    component: ClusterHostGroupDetailsPage,
  },
  {
    name: 'Service Health',
    link: { path: '/health/:tab?', exact: true },
    component: ServiceHealthDetailsPage,
  },

  ...kubernetesRoutesMapping.map((route) => ({
    ...route,
    requiredFeatures: (features) => features?.kaapi?.enable_kaapi !== false,
  })),
]

export const openstackNavItems = ({ includeVMwareGateway = false }) => [
  {
    name: 'Home',
    link: { path: '/dashboard', definition: '/dashboard' },
    icon: 'house',
  },
  {
    name: 'Infrastructure',
    icon: 'buildings',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: {
      path: '/infrastructure/hosts/list',
      definition: '/infrastructure/hosts/:tab/:id?',
    },
    nestedLinks: [
      {
        name: 'Cluster Blueprint',
        link: {
          path: '/infrastructure/blueprint',
          definition: '/infrastructure/blueprint',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
      {
        name: 'Clusters',
        link: {
          path: '/infrastructure/clusters',
          definition: '/infrastructure/clusters',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
      {
        name: 'Cluster Hosts',
        link: {
          path: '/infrastructure/hosts/list',
          definition: '/infrastructure/hosts/:tab/:id?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
      // VMware Gateway
      ...(includeVMwareGateway
        ? [
            {
              name: 'VMware Gateway',
              link: {
                path: '/infrastructure/hosts/list',
                definition: '/infrastructure/hosts/:tab/:id?',
              },
              requiredFeatures: (features) => {
                return features?.experimental?.pmov2_du_type === 'vmware'
              },
            },
          ]
        : []),
      {
        name: 'GPU Hosts',
        link: {
          path: '/infrastructure/gpu/hosts',
          definition: '/infrastructure/gpu/:tab?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
      {
        name: 'Host Aggregates',
        link: {
          path: '/infrastructure/host-aggregates',
          definition: '/infrastructure/host-aggregates/:id?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
      {
        name: 'Stretched Clusters',
        link: {
          path: '/infrastructure/stretched-cluster',
          definition: '/infrastructure/stretched-cluster/:id?/:tab?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
    ],
  },
  {
    name: 'Virtual Machines',
    icon: 'desktop',
    link: { path: '/compute/vms/list', definition: '/compute/vms/:tab/:id?' },
    nestedLinks: [
      {
        name: 'Virtual Machines',
        link: {
          path: '/compute/vms/list',
          definition: '/compute/vms/:tab/:id?',
        },
      },
      {
        name: 'Server Groups',
        link: {
          path: '/compute/server-groups',
          definition: '/compute/server-groups',
        },
        requiredPermissions: [
          ['nova', 'os_compute_api.os_server_groups.index:GET:/os-server-groups'],
          ['nova', 'os_compute_api.os_server_groups.create:POST:/os-server-groups'],
          [
            'nova',
            'os_compute_api.os_server_groups.delete:DELETE:/os-server-groups/{server_group_id}',
          ],
        ],
      },
      {
        name: 'Images and VM Snapshots',
        link: {
          path: '/images/images',
          definition: '/images/:tab?',
        },
        requiredPermissions: [
          ['glance', 'get_images:GET:/v2/images'],
          ['glance', 'add_image:POST:/v2/images'],
          ['glance', 'modify_image:PATCH:/v2/images/{image_id}'],
          ['glance', 'delete_image:DELETE:/v2/images/{image_id}'],
        ],
      },
      {
        name: 'Flavors',
        link: {
          path: '/flavors',
          definition: '/flavors',
        },
        requiredPermissions: [
          ['nova', 'os_compute_api.os_flavor_manage.show:GET:/flavors/{flavor_id}'],
          ['nova', 'os_compute_api.os_flavor_manage.create:POST:/flavors'],
          ['nova', 'os_compute_api.os_flavor_manage.update:PUT:/flavors/{flavor_id}'],
          ['nova', 'os_compute_api.os_flavor_manage.delete:DELETE:/flavors/{flavor_id}'],
        ],
      },
      {
        name: 'SSH Keys',
        link: {
          path: '/ssh-key',
          definition: '/ssh-key/:id?',
        },
        requiredPermissions: [
          ['nova', 'os_compute_api.os_keypairs.index:GET:/os-keypairs'],
          ['nova', 'os_compute_api.os_keypairs.create:POST:/os-keypairs'],
          ['nova', 'os_compute_api.os_keypairs.delete:DELETE:/os-keypairs/{keypair_name}'],
        ],
      },
    ],
  },
  {
    name: 'Storage',
    icon: 'coins',
    link: { path: '/storage/dashboard', definition: '/storage/:tab' },
    nestedLinks: [
      {
        name: 'Dashboard',
        link: {
          path: '/storage/dashboard',
          definition: '/storage/dashboard',
        },
      },
      {
        name: 'Volumes',
        link: {
          path: '/storage/volumes',
          definition: '/storage/volumes',
        },
        requiredPermissions: [
          ['cinder', 'volume.get_all:GET:/volumes'],
          ['cinder', 'volume.create:POST:/volumes'],
          ['cinder', 'volume.update:PUT/POST:/volumes'],
          ['cinder', 'volume.delete:DELETE:/volumes/{volume_id}'],
        ],
      },
      {
        name: 'Volume Types',
        link: {
          path: '/storage/volume-types',
          definition: '/storage/volume-types',
        },
        requiredPermissions: [
          ['cinder', 'volume_extension.type_get_all:GET:/types/'],
          ['cinder', 'volume_extension.type_create:POST:/types'],
          ['cinder', 'volume_extension.type_update:PUT:/types'],
          ['cinder', 'volume_extension.type_delete:DELETE:/types'],
        ],
      },
      {
        name: 'Volume Snapshots',
        link: {
          path: '/storage/snapshots',
          definition: '/storage/snapshots',
        },
        requiredPermissions: [
          ['cinder', 'volume.get_all_snapshots:GET:/snapshots'],
          ['cinder', 'volume.create_snapshot:POST:/snapshots'],
          ['cinder', 'volume.update_snapshot:PUT:/snapshots/{snapshot_id}'],
          ['cinder', 'volume.delete_snapshot:DELETE:/snapshots/{snapshot_id}'],
        ],
      },
    ],
  },
  {
    name: 'Networks and Security',
    icon: 'network-wired',
    link: { path: '/networking/networks', definition: '/networking/:tab' },
    nestedLinks: [
      {
        name: 'Networks',
        link: {
          path: '/networking/networks',
          definition: '/networking/networks/:id?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
        requiredPermissions: [
          ['neutron', 'get_network:GET:/networks'],
          ['neutron', 'create_network:POST:/networks'],
          ['neutron', 'update_network:PUT:/networks/{id}'],
          ['neutron', 'delete_network:DELETE:/networks/{id}'],
        ],
      },

      {
        name: 'Networks',
        link: {
          path: '/networking/vmw-networks',
          definition: '/networking/vmw-networks/:id?',
        },
        requiredFeatures: (features) => features?.experimental?.pmov2_du_type === 'vmware',
        requiredPermissions: [
          ['neutron', 'get_network:GET:/networks'],
          ['neutron', 'create_network:POST:/networks'],
          ['neutron', 'update_network:PUT:/networks/{id}'],
          ['neutron', 'delete_network:DELETE:/networks/{id}'],
        ],
      },
      {
        name: 'Routers',
        link: {
          path: '/networking/routers',
          definition: '/networking/routers/:id?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
        requiredPermissions: [
          ['neutron', 'get_router.ha:GET:/routers'],
          ['neutron', 'create_router:POST:/routers'],
          ['neutron', 'update_router:PUT:/routers/{id}'],
          ['neutron', 'delete_router:DELETE:/routers/{id}'],
        ],
      },
      {
        name: 'Public IPs',
        link: {
          path: '/networking/public-ips',
          definition: '/networking/public-ips/:id?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
      {
        name: 'Security Groups',
        link: {
          path: '/networking/security-groups',
          definition: '/networking/security-groups/:id?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },

      {
        name: 'Load Balancers',
        link: {
          path: '/networking/load-balancers/loadBalancers',
          definition: '/networking/load-balancers/:tab?',
        },
        requiredFeatures: (features) => {
          return !(features?.experimental?.pmov2_du_type === 'vmware')
        },
      },
      {
        name: 'DNS',
        link: {
          path: '/networking/dns/pools?view=list',
          definition: '/networking/dns/:tab',
        },
      },
    ],
  },
  {
    name: 'Orchestration',
    icon: 'cubes',
    link: { path: '/orchestration/repository', definition: '/orchestration/:tab' },
    // AFC-FR-004: See AppCatalogFeatureGate.spec.md
    requiredFeatures: (features) => features?.app_catalog?.enabled === true,
    nestedLinks: [
      {
        name: 'Git Repository',
        link: {
          path: '/orchestration/repository',
          definition: '/orchestration/repository',
        },
        // AFC-FR-001: See AppCatalogFeatureGate.spec.md
        requiredFeatures: (features) => features?.app_catalog?.enabled === true,
      },
      {
        name: 'Apps',
        isBetaVersion: true,
        betaVersionTooltip: 'This feature is in Beta and subject to change. We welcome feedback.',
        link: {
          path: '/orchestration/apps/myapps',
          definition: '/orchestration/apps/:tab?',
        },
        // AFC-FR-001: See AppCatalogFeatureGate.spec.md
        requiredFeatures: (features) => features?.app_catalog?.enabled === true,
      },
    ],
  },
  ...[kubernetesNav],
]

openstackPlugin.registerRoutes(openstackRoutes)
openstackPlugin.registerNavItems(openstackNavItems({ includeVMwareGateway: false }))

export default openstackPlugin
